import { setActiveCategories } from './state/actions';
import type { KeyLevelsAction } from './state/actions';
import { deriveAvailableCategories, reducer } from './state/reducer';
import { createInitialState } from './state/types';
import type { ConfigRow, KeyLevelLadderRow, KeyLevelsState } from './state/types';
import { computed, createModel, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import { Slugs } from '@thinkalpha/common/constants/slugs.js';
import { EmptyArray } from '@thinkalpha/common/util/iterators.js';
import type { IndicatorImportRef } from '@thinkalpha/platform-ws-client/contracts/dictionary.js';
import type { OutputColumn } from '@thinkalpha/platform-ws-client/contracts/table.js';
import type {
    AlphaEyeKeyLevel,
    AlphaEyeKeyLevelCategory,
} from '@thinkalpha/platform-ws-client/contracts/widget/alpha-eye.js';
import { escapeFormulaStringLiteral } from 'src/lib/escapeFormulaStringLiteral';
import { createTable } from 'src/lib/table';
import { FilterStage, OutputStage, SourceStage } from 'src/lib/tableStages';
import type { Stage } from 'src/lib/tableStages';
import type { TableModel } from 'src/models/TableModel';
import type { ReactBindings } from 'src/types/bindings';
import { v4 } from 'uuid';

const LAST_COLUMN_ID = 'kl-row::last';
const indicatorColumnId = (indicatorId: string) => `kl-row::${indicatorId}`;
const formulaColumnId = (rowId: string) => `kl-row::formula::${rowId}`;

/** Column id a row's live value will appear under in the TableModel update map. */
function columnIdFor(row: ConfigRow): string {
    return row.kind === 'formula' ? formulaColumnId(row.id) : indicatorColumnId(row.indicatorId);
}

function generateLocalId(): string {
    return v4().replace(/-/g, '');
}

function uniqueIds(ids: readonly string[]): readonly string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const id of ids) {
        if (seen.has(id)) continue;
        seen.add(id);
        out.push(id);
    }
    return out;
}

interface KeyLevelsModelInit {
    slugMapServiceProvider: ReactBindings['SlugMapServiceProvider'];
    /**
     * Pre-built TableModel for the per-level WS subscription. Parent owns its
     * lifetime — pass it through a `DisposableStack.use(...)` alongside this
     * model so both get disposed together. The factory wires reactive
     * queryOptions into it; the parent must not call `init(...)` on it again.
     */
    tableModel: TableModel;
    /** Initial chip-filter state. `null` means "no filter applied" — every chip reads as active. */
    initialActiveCategories: ReadonlySet<AlphaEyeKeyLevelCategory> | null;
    symbol: ReadonlySignal<string>;
    enabled: ReadonlySignal<boolean>;
    keyLevels: ReadonlySignal<readonly AlphaEyeKeyLevel[]>;
}

/**
 * Model-driven (ADR 0007) data layer for the AlphaEye Key Levels tab. Consumes
 * parent-owned symbol/enabled/key-level inputs, owns only chip-filter state via
 * a pure reducer, drives the supplied TableModel to subscribe to every level's
 * indicator (plus the symbol's LAST) for the current ticker, and exposes preact
 * signals for filter state, loading/error/empty, and the transformed ladder
 * rows.
 *
 * Reactivity contract: 100% signal-driven. Parent-owned inputs are consumed as
 * `ReadonlySignal<T>` values, chip-filter state flows through `#dispatch`, and
 * outputs are `ReadonlySignal<T>` derivations. Live WS updates land on the
 * underlying `TableModel.updateSignal` and cascade through `computed()` chains
 * automatically — consumers (Svelte) just read `$rows` etc.
 */
export const KeyLevelsModel = createModel((init: KeyLevelsModelInit) => {
    const state = signal<KeyLevelsState>(createInitialState({ activeCategories: init.initialActiveCategories }));
    const dispatch = (action: KeyLevelsAction): void => {
        state.value = reducer(state.peek(), action);
    };
    const { symbol, enabled, keyLevels } = init;

    // Identity-keyed WeakMap memo for stable id assignment. Lives in factory
    // closure scope (not reducer state) — it's keyed by AlphaEyeKeyLevel object
    // identity, which isn't serialisable/equality-checkable inside a reducer.
    // Same input AlphaEyeKeyLevel keeps the same id across re-emits so
    // id-keyed `{#each}` blocks in Svelte don't churn.
    const idMemo = new WeakMap<AlphaEyeKeyLevel, string>();

    const configRows = computed<readonly ConfigRow[]>(() => {
        const levels = keyLevels.value;
        if (levels.length === 0) return EmptyArray;
        return levels.map<ConfigRow>((level) => {
            let id = idMemo.get(level);
            if (!id) {
                id = generateLocalId();
                idMemo.set(level, id);
            }
            if (level.type === 'formula') {
                return {
                    kind: 'formula',
                    id,
                    name: level.name,
                    category: level.category,
                    formula: level.formula,
                    imports: level.imports,
                };
            }
            return {
                kind: 'indicator',
                id,
                name: level.name,
                category: level.category,
                indicatorId: level.indicator.id,
            };
        });
    });

    const availableCategories = computed<readonly AlphaEyeKeyLevelCategory[]>(() =>
        deriveAvailableCategories(keyLevels.value),
    );

    // ── Live TableModel — one query, one column per unique indicator + LAST.
    // Reactive queryOptions: the query rebuilds whenever symbol or the
    // indicator-id set changes, and stays dormant while !enabled or no rows.
    const { slugMapServiceProvider, tableModel } = init;
    // True iff we're actively requesting data — drives `loading` and `error`
    // so we don't spin forever or surface a "failed to load" message when the
    // model is idle (disabled, no symbol, or no levels configured). Must
    // match the `isActive` gate used inside `queryOptions` below, otherwise
    // the chrome lights up for a query that will never fire.
    const isQueryActive = computed(() => enabled.value && Boolean(symbol.value) && keyLevels.value.length > 0);
    const queryOptions = computed(() => {
        const currentSymbol = symbol.value;
        const currentEnabled = enabled.value;
        const rows = configRows.value;
        const indicatorRows = rows.filter(
            (r): r is Extract<ConfigRow, { kind: 'indicator' }> => r.kind === 'indicator',
        );
        const formulaRows = rows.filter((r): r is Extract<ConfigRow, { kind: 'formula' }> => r.kind === 'formula');
        const uniqueIndicatorIds = uniqueIds(indicatorRows.map((r) => r.indicatorId));
        const formulaRowIds = formulaRows.map((r) => r.id);
        const isActive = currentEnabled && Boolean(currentSymbol) && uniqueIndicatorIds.length + formulaRows.length > 0;
        return {
            // queryKey includes both id sets so adding/removing/replacing
            // rows of either kind rebuilds the underlying subscription.
            queryKey: [
                'alpha-eye-key-levels',
                currentSymbol,
                uniqueIndicatorIds.join(','),
                formulaRowIds.join(','),
            ] as readonly unknown[],
            gcTime: 0,
            staleTime: 0,
            enabled: isActive,
            queryFn: async () => {
                if (!isActive) return null;
                const slugMapService = await slugMapServiceProvider();
                const slugMap = slugMapService.slugMap.value;
                const symbolIndicator = slugMap[Slugs.symbol];
                const lastIndicator = slugMap[Slugs.last];

                const symbolRef: IndicatorImportRef = {
                    id: symbolIndicator.id,
                    alias: symbolIndicator.symbol,
                };
                const columns: OutputColumn[] = [
                    { type: 'indicator', id: LAST_COLUMN_ID, indicator: { id: lastIndicator.id } },
                    ...uniqueIndicatorIds.map<OutputColumn>((indicatorId) => ({
                        type: 'indicator',
                        id: indicatorColumnId(indicatorId),
                        indicator: { id: indicatorId },
                    })),
                    ...formulaRows.map<OutputColumn>((row) => ({
                        type: 'formula',
                        id: formulaColumnId(row.id),
                        formula: row.formula,
                        imports: row.imports.map((imp) => ({ id: imp.id, alias: imp.alias })),
                    })),
                ];

                let stage: Stage = SourceStage.allForKey('ticker');
                stage = FilterStage.fromInputStage(stage, {
                    type: 'filter',
                    formula: `${symbolIndicator.symbol}=='${escapeFormulaStringLiteral(currentSymbol)}'`,
                    imports: [symbolRef],
                });
                stage = OutputStage.fromInputStage(stage, {}, columns);
                return createTable({ leafStage: stage.build() });
            },
        };
    });
    tableModel.init({ queryOptions, initBounds: { firstRow: 0, windowSize: 1 }, initThrottle: 1000 });

    // ── lastPrice — read the LAST column out of every WS tick.
    const lastPrice = computed<number | null>(() => {
        const value = tableModel.updateSignal.value?.update?.[LAST_COLUMN_ID];
        return typeof value === 'number' && Number.isFinite(value) ? value : null;
    });

    // ── loading / error mirrors of the TableModel's connection status.
    // Stays true through the full waiting window: while the table is
    // connecting AND once connected, until the first WS tick lands. Gated on
    // `isQueryActive` so we don't show "loading" when nothing is being
    // requested (disabled tab, or no levels configured for the symbol).
    const loading = computed<boolean>(() => {
        if (!isQueryActive.value) return false;
        if (tableModel.connectionStatus.value !== 'connected') return true;
        if (tableModel.updateSignal.value?.update == null) return true;
        return false;
    });
    const error = computed<string | null>(() => {
        // Mirror the `isQueryActive` short-circuit used by `loading`: if no
        // request can run (no symbol / no levels / disabled) we must not
        // surface a stale `disconnected` status as a load error.
        if (!isQueryActive.value) return null;
        // TableStatus has no explicit 'error' value; `'disconnected'` is the
        // only terminal failure state. Loading/uninit don't qualify.
        return tableModel.connectionStatus.value === 'disconnected' ? 'Failed to load levels' : null;
    });

    // ── rows — the ladder. Built in three passes (project / sort / insert).
    const rows = computed<readonly KeyLevelLadderRow[]>(() => {
        const cfg = configRows.value;
        if (cfg.length === 0) return EmptyArray;

        const update = tableModel.updateSignal.value?.update;
        if (!update) return EmptyArray;

        const active = state.value.activeCategories;
        const last = lastPrice.value;

        // 1. Project.
        const levels: Extract<KeyLevelLadderRow, { kind: 'level' }>[] = [];
        for (const row of cfg) {
            if (active !== null && !active.has(row.category)) continue;
            const price = update[columnIdFor(row)];
            if (typeof price !== 'number' || !Number.isFinite(price)) continue;
            const delta = last !== null ? price - last : null;
            const deltaPercent = last !== null && last !== 0 ? (price - last) / last : null;
            const classification: 'support' | 'resistance' = last !== null && price > last ? 'resistance' : 'support';
            levels.push({
                kind: 'level',
                id: row.id,
                name: row.name,
                category: row.category,
                price,
                delta,
                deltaPercent,
                classification,
            });
        }

        // 2. Sort desc by price; stable tie-break by id so equal-price rows
        // have a deterministic order.
        levels.sort((a, b) => b.price - a.price || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

        // 3. Thread in the current-price marker at the first rung where
        // level.price < last. If LAST is unavailable, no marker is emitted.
        if (last === null) return levels;

        const ladder: KeyLevelLadderRow[] = [];
        let inserted = false;
        for (const lvl of levels) {
            if (!inserted && lvl.price < last) {
                ladder.push({ kind: 'current', price: last });
                inserted = true;
            }
            ladder.push(lvl);
        }
        if (!inserted) ladder.push({ kind: 'current', price: last });
        return ladder;
    });

    const isEmpty = computed<boolean>(() => {
        if (loading.value) return false;
        for (const row of rows.value) {
            if (row.kind === 'level') return false;
        }
        return true;
    });

    return {
        symbol,
        enabled,
        activeCategories: computed(() => state.value.activeCategories),
        availableCategories,
        lastPrice,
        loading,
        error,
        rows,
        isEmpty,
        toggleCategory(category: AlphaEyeKeyLevelCategory): void {
            const activeCategories = state.value.activeCategories;
            if (activeCategories === null) {
                const next = new Set<AlphaEyeKeyLevelCategory>(deriveAvailableCategories(keyLevels.value));
                next.delete(category);
                dispatch(setActiveCategories(next));
                return;
            }
            const next = new Set(activeCategories);
            if (next.has(category)) next.delete(category);
            else next.add(category);
            dispatch(setActiveCategories(next));
        },
        clearCategoryFilter(): void {
            dispatch(setActiveCategories(null));
        },
        refetch(): void {
            tableModel.refetch();
        },
    };
});

export type KeyLevelsModel = InstanceType<typeof KeyLevelsModel>;

export type { KeyLevelLadderRow } from './state/types';
